/**
 * The store-layer contract for an expected 4xx.
 *
 * The defect this replaces: every store function in region.js caught a 4xx and
 * *resolved* with `error.response.data.message` — a String — where the caller
 * was expecting an Activity/Course/submission object. Nothing about the call
 * signalled that, so a 403 or 404 turned into a TypeError several frames away
 * (region.js's GetActivity -> Activity.vue's loadActivityFromServer reading
 * `.activity` off a string), or into plausible-looking wrong behaviour when the
 * caller happened to guard with optional chaining. Consumers had been patched
 * one at a time with `typeof data === 'string'` checks; this fixes the producer.
 *
 * The contract is now: a store function either RESOLVES with its documented
 * success value, or REJECTS. It never resolves with something of a different
 * type. A 4xx rejects with an ApiClientError carrying the server's message,
 * status and body; anything else rejects with the original axios error
 * untouched.
 *
 * Why reject rather than return a discriminated `{ ok, error }` result: the
 * catch blocks in these stores already end in `throw error` for every status
 * that isn't 4xx, so rejecting makes the 4xx path join the control flow that is
 * already there instead of adding a second, parallel one. It also leaves every
 * success return statement in region.js untouched — a result object would have
 * required rewriting all ~80 of them and every caller, for the same guarantee.
 *
 * NOTE the deliberate scope limit: user.js's authentication surface
 * (VerifyUser, CreateOTP, VerifyOTP, BecomeUser, Logout) still returns the
 * message string. Those run on `api_no_intercept`, which by design raises no
 * toast, so there the returned string IS the user-facing notification and ~13
 * callers discriminate on it explicitly. That is a working contract, not this
 * defect, and converting it is a separate UX change.
 */

import { markApiErrorReported } from './apiErrorReporting';

/**
 * An expected client error (4xx) from a ChartFlow API call.
 *
 * `message` is the server's own message, so a caller that used to render the
 * returned string can render `error.message` and show the identical text.
 *
 * ONE deliberate text change: when the server sends an EMPTY message, the old
 * contract resolved `''` and callers rendered an empty toast — a negative
 * notification with nothing in it, which tells the user only that something
 * broke. This falls back to the status line instead. Chosen because an Error
 * with an empty message is also useless in Datadog and in a stack trace, and
 * because an empty-bodied 4xx is a backend bug we want legible rather than
 * invisible. Real messages are passed through untouched, so no observed copy
 * changes — see RubricTemplateEditor.clientError.test.js.
 */
export class ApiClientError extends Error {
  constructor(axiosError) {
    const response = axiosError?.response;
    super(
      response?.data?.message ||
        `Request failed with status code ${response?.status}`
    );
    this.name = 'ApiClientError';
    this.status = response?.status;
    this.data = response?.data;
    // Keep the original axios error reachable for anything that needs the
    // request config (endpoint, method) — e.g. ad-hoc debugging.
    this.cause = axiosError;
  }
}

/**
 * Is this axios rejection an expected client error this contract owns?
 *
 * 4xx EXCEPT 401. A 401 is not a message for the caller to render — it means
 * the session expired and restapi.js's handle401 is already toasting,
 * redirecting to /login and clearing session state. handle401 stamps that
 * error with markApiErrorHandled, and views gate on isApiErrorHandled to stay
 * quiet about it (DashboardView, StudentDashboard — GitLab #895). Wrapping it
 * would hand those views a NEW error object carrying only the REPORTED stamp,
 * so isApiErrorHandled would read false and they would stack a second toast on
 * handle401's and re-report the issue that #895 removed. The two stamps mean
 * different things; the only way to keep them straight here is to leave the
 * 401 object handle401 produced completely alone.
 */
export function isClientError(error) {
  const status = error?.response?.status;
  return typeof status === 'number' && status >= 400 && status < 500 && status !== 401;
}

/**
 * Map an axios rejection to what a store should throw.
 *
 * A 4xx other than 401 becomes an ApiClientError; everything else (401, 5xx,
 * network failures, cancellations) passes through unchanged so restapi.js's
 * error_intercept, handle401 and apiErrorReporting.js keep owning those paths
 * exactly as before — including both of the stamps they set.
 *
 * The ApiClientError is stamped with markApiErrorReported for the same reason
 * error_intercept stamps the axios error it re-throws: boot/rum.js drops RUM's
 * duplicate capture of an already-triaged failure by looking for that stamp.
 * Without it, an ApiClientError reaching RUM would be an unstamped error whose
 * message is the *server's* text — so it would miss both of
 * shouldDiscardRumErrorEvent's filters and mint a brand-new Error Tracking
 * issue per distinct server message. Stamping keeps Datadog behaviour byte for
 * byte what it was before this change: the enriched report from
 * error_intercept, and nothing else.
 */
export function toStoreError(error) {
  if (!isClientError(error)) return error;
  return markApiErrorReported(new ApiClientError(error));
}
